import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";

import Logo from "../../assests/logo/hipship.png";
import SideMenu, { SIDEBAR_COLLAPSED, SIDEBAR_EXPANDED } from "./SideMenu";
import { isMac } from "./sidebar/CommandPalette";

interface Props {
  /** true = slim icon rail that opens over the page on hover; false = kept open beside the page. */
  collapsed: boolean;
  onToggle: () => void;
  onSearch: () => void;
}

const TOGGLE_SHORTCUT = isMac ? "⌘B" : "Ctrl B";
// Small delays so the menu doesn't flicker open when the pointer just crosses it
const OPEN_DELAY = 120;
const CLOSE_DELAY = 180;

const SideBar = ({ collapsed, onToggle, onSearch }: Props) => {
  const [hovering, setHovering] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!collapsed) setHovering(false);
  }, [collapsed]);

  const enter = () => {
    if (!collapsed) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHovering(true), OPEN_DELAY);
  };
  const leave = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHovering(false), CLOSE_DELAY);
  };

  const expanded = !collapsed || hovering;
  const docked = !collapsed;

  return (
    <aside
      className={`sb-shell ${expanded ? "" : "is-collapsed"} ${collapsed && hovering ? "is-floating" : ""}`}
      style={{ width: expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED }}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && leave()}
      onKeyDown={(e) => e.key === "Escape" && setHovering(false)}
    >
      <div className="sb-head">
        <NavLink to="/" className="sb-brand no-hover" aria-label="Hipship home">
          {expanded ? <img draggable={false} src={Logo} alt="Hipship" /> : <span className="sb-mark">h</span>}
        </NavLink>
      </div>

      <div className="sb-scroll">
        <SideMenu collapsed={!expanded} onExpand={() => setHovering(true)} onSearch={onSearch} />
      </div>

      <div className="sb-foot">
        <button
          type="button"
          className={`sb-item ${docked ? "on" : ""}`}
          onClick={onToggle}
          aria-pressed={docked}
          title={docked ? "Let the menu hide and open on hover" : "Keep the menu open beside the page"}
        >
          <span className="sb-icon">{docked ? <PushPinIcon /> : <PushPinOutlinedIcon />}</span>
          <span className="sb-label">{docked ? "Auto-hide menu" : "Keep menu open"}</span>
          <kbd className="sb-kbd">{TOGGLE_SHORTCUT}</kbd>
        </button>
      </div>
    </aside>
  );
};

export default SideBar;
